import { useEffect, useRef, useState } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { TwilioService } from '../modules/twilio/service/twilioService';

const twilioService = new TwilioService();

export type CallStatus = 'registering' | 'registered' | 'calling' | 'connected' | 'ended' | 'failed';

type Options = {
  identity: string;
  from: string;
  refreshMinutes?: number;
  logLevel?: 'error' | 'warn' | 'info' | 'debug' | 'silent';
};

export function useTwilioDevice({
  identity,
  from,
  refreshMinutes = 50,
  logLevel = 'error',
}: Options) {
  const [device, setDevice] = useState<Device | null>(null);
  const [status, setStatus] = useState<CallStatus>('registering');
  const [statusText, setStatusText] = useState('Starting…');
  const [isMuted, setIsMuted] = useState(false);
  const callRef = useRef<Call | null>(null);
  const refreshTimer = useRef<number | null>(null);

  const fetchToken = async () => {
    const response = await twilioService.getToken(identity);
    return response.data as string;
  };

  useEffect(() => {
    let d: Device | null = null;
    const destroyed = false;

    (async () => {
      try {
        setStatus('registering');
        setStatusText('Registering device');
        const token = await fetchToken();

        d = new Device(token, { logLevel });

        d.on('registered', () => {
          setStatus('registered');
          setStatusText('registered');
        });
        d.on('unregistered', () => setStatusText('Device unregistered'));
        d.on('error', (e) => {
          setStatus('failed');
          setStatusText(e?.message ?? 'Unknown error');
        });
        d.on('disconnect', () => {
          callRef.current = null;
          setIsMuted(false);
          setStatus('ended');
          setStatusText('ended');
        });

        await d.register();
        if (!destroyed) setDevice(d);

        const ms = Math.max(5, refreshMinutes) * 60 * 1000;
        refreshTimer.current = window.setInterval(async () => {
          try {
            const newToken = await fetchToken();
            await d?.updateToken(newToken);
          } catch {
            console.log('error refreshing Twilio token');
          }
        }, ms) as unknown as number;
      } catch (e: any) {
        setStatus('failed');
        setStatusText(e?.message ?? 'Init error');
      }
    })();

    return () => {
      if (refreshTimer.current) window.clearInterval(refreshTimer.current);
      try {
        d?.unregister();
        d?.destroy();
      } catch {
        console.log("error destroying Twilio Device");
      }
    };
  }, [identity, logLevel, refreshMinutes]);

  const connect = async (
    to: string,
    extraParams?: Record<string, string | number | boolean | undefined>
  ) => {
    if (!device) return;
    try {
      const c = await device.connect({
        params: { To: to, From: from, ...(extraParams ?? {}) },
      });
      callRef.current = c;

      c.on('ringing', (hasEarlyMedia: boolean) => {
        setStatus('calling');
        setStatusText('Calling…');
      });

      c.on('accept', () => {
        setStatus('connected');
        setStatusText('connected');
      });

      c.on('disconnect', () => {
        callRef.current = null;
        setIsMuted(false);
        setStatus('ended');
        setStatusText('Call ended');
      });

      c.on('error', (e) => {
        setStatus('failed');
        setStatusText(e?.message ?? 'Call error');
      });
    } catch (e: any) {
      setStatus('failed');
      setStatusText(e?.message ?? 'Call error');
    }
  };

  const hangup = () => {
    try {
      callRef.current?.disconnect();
    } catch {
      console.log('error hanging up call');
    }
  };

  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    try {
      callRef.current?.mute(next);
    } catch {
      console.log('error toggling mute');
    }
  };

  const hasActiveCall = !!callRef.current && (status === 'calling' || status === 'connected');
  const deviceReady = !!device && status !== 'registering' && status !== 'failed';

  return {
    deviceReady,
    status,
    statusText,
    isMuted,
    hasActiveCall,
    connect,
    hangup,
    toggleMute,
  };
}
